import React, { useState, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { analyticsAPI, clicksAPI, gadsScriptAPI } from '../utils/api';

const REFRESH_MS = 120000;
const TABS = ['Blocked IPs', 'Countries', 'Keywords', 'Campaigns', 'Multi-Clicks', 'IP Ranges', 'Devices'];
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const FRAUD_TYPE_COLORS = {
  'Over Threshold': '#f97316', 'Fraudulent Device': '#f59e0b', 'Disabled JS': '#60a5fa',
  'VPN': '#3b82f6', 'Manually Blocked': '#a855f7', 'Aggressive Approach': '#ec4899',
  'Range Blocking': '#ef4444', 'Whitelisted': '#9ca3af', 'Bounced': '#fb923c',
  'Out of Geo': '#eab308', 'AI': '#22c55e', 'Not Specified': '#86efac',
};
const FRAUD_COLORS = Object.values(FRAUD_TYPE_COLORS);

const SIGNAL_LABELS = {
  vpn_detected: 'Used VPN software', proxy_detected: 'Used VPN software',
  tor_detected: 'Used VPN software', datacenter_ip: 'Used VPN software',
  excessive_clicks_10min: 'Over click threshold', excessive_hourly_clicks: 'Over click threshold',
  excessive_daily_clicks: 'Over click threshold', excessive_weekly_clicks: 'Over click threshold',
  excessive_monthly_clicks: 'Over click threshold', previously_blocked: 'Previously blocked IP',
  suspicious_user_agent: 'Fraudulent device', crawler_detected: 'Crawler detected',
  no_referrer: 'Bounced visit', suspicious_ip_range: 'Out of geo target',
  disabled_js: 'Disabled JavaScript', manual_block: 'Manually blocked',
  ip_range_block: 'IP range blocked', aggressive_pattern: 'Aggressive approach',
  ai_detected: 'AI detected',
};

function getDetectionReason(click) {
  const signals = click.signals || [];
  if (signals.length > 0) {
    const sig = typeof signals[0] === 'string' ? signals[0] : String(signals[0]);
    return SIGNAL_LABELS[sig] || sig.replace(/_/g, ' ');
  }
  if (click.is_fraud) return 'Fraud detected';
  return 'Clean';
}

/* Deep-normalize: convert any nested Firestore objects (Timestamps, etc.) to primitives */
function deepNormalize(val) {
  if (val === null || val === undefined) return val;
  if (typeof val !== 'object') return val;
  if (Array.isArray(val)) return val.map(deepNormalize);
  // Firestore Timestamp: has _seconds and _nanoseconds
  if (val._seconds !== undefined || val.seconds !== undefined) {
    const secs = val._seconds ?? val.seconds ?? 0;
    return new Date(secs * 1000).toISOString();
  }
  const out = {};
  for (const [k, v] of Object.entries(val)) {
    out[k] = deepNormalize(v);
  }
  return out;
}

/* CSV download helper */
function downloadCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map(row => headers.map(h => {
      const val = row[h] ?? '';
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
    }).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* Pagination */
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= Math.min(totalPages, 5); i++) pages.push(i);
  if (totalPages > 5) pages.push('...');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', marginTop: 16 }}>
      <button className="btn btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }} disabled={page <= 1} onClick={() => onChange(1)}>&#171;</button>
      <button className="btn btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }} disabled={page <= 1} onClick={() => onChange(page - 1)}>&#8249;</button>
      {pages.map((p, i) => p === '...' ? <span key={i} style={{ padding: '0 4px', color: 'var(--text-muted)' }}>...</span> : (
        <button key={p} onClick={() => onChange(p)} style={{
          width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          background: p === page ? 'var(--accent-blue)' : 'transparent',
          color: p === page ? '#fff' : 'var(--text-secondary)',
        }}>{p}</button>
      ))}
      <button className="btn btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }} disabled={page >= totalPages} onClick={() => onChange(page + 1)}>&#8250;</button>
      <button className="btn btn-ghost" style={{ padding: '4px 8px', fontSize: 11 }} disabled={page >= totalPages} onClick={() => onChange(totalPages)}>&#187;</button>
    </div>
  );
}

/* Download buttons */
function DownloadButtons({ onCSV, onBlock }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button onClick={onCSV} className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 12, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
        Download CSV
      </button>
      {onBlock && (
        <button onClick={onBlock} className="btn btn-ghost" style={{ padding: '6px 14px', fontSize: 12, borderColor: 'var(--accent-orange)', color: 'var(--accent-orange)' }}>
          Download Block Report
        </button>
      )}
    </div>
  );
}

/* Search bar */
function SearchBar({ value, onChange, placeholder, searchBy, onSearchByChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
      <div style={{ position: 'relative', flex: '1 1 300px' }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14 }}>&#128269;</span>
        <input
          value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder || 'Search...'}
          style={{ paddingLeft: 32, width: '100%', fontSize: 13 }}
        />
      </div>
      {onSearchByChange && (
        <select value={searchBy} onChange={e => onSearchByChange(e.target.value)} style={{ width: 'auto', fontSize: 12 }}>
          <option value="ip">Search by IP</option>
          <option value="country">Search by Country</option>
          <option value="reason">Search by Reason</option>
        </select>
      )}
    </div>
  );
}

function AnalyticsRangePicker({ onApply }) {
  const [range, setRange] = React.useState('30');
  const [start, setStart] = React.useState('');
  const [end, setEnd] = React.useState('');
  const fmt = d => d.toISOString().slice(0, 10);
  const fromDays = (n) => { const t = new Date(), s = new Date(t); s.setDate(s.getDate() - n); return { start: fmt(s), end: fmt(t), days: n }; };

  const handleRange = (v) => {
    setRange(v);
    if (v !== 'custom') { const d = fromDays(Number(v)); onApply(d.start, d.end, d.days); }
  };
  const handleApply = () => {
    if (start && end) {
      const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000));
      onApply(start, end, days);
    }
  };
  const s = { padding: '7px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text-primary)', outline: 'none' };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={range} onChange={e => handleRange(e.target.value)} style={s}>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="365">Last 12 months</option>
        <option value="custom">Custom</option>
      </select>
      {range === 'custom' && (
        <>
          <input type="date" value={start} onChange={e => setStart(e.target.value)} style={s} />
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)} style={s} />
          <button onClick={handleApply} disabled={!start || !end}
            style={{ padding: '7px 16px', background: start && end ? 'var(--accent-blue)' : 'var(--bg-secondary)', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: start && end ? '#fff' : 'var(--text-muted)', cursor: start && end ? 'pointer' : 'default' }}>
            Apply
          </button>
        </>
      )}
    </div>
  );
}

function TabLoader() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
      {[...Array(5)].map((_, i) => (
        <div key={i} className="skeleton" style={{ height: 48, borderRadius: 10, animationDelay: `${i * 80}ms` }} />
      ))}
    </div>
  );
}

export default function FraudAnalytics({ campaignId }) {
  const [activeTab, setActiveTab] = useState('Blocked IPs');
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [fetchStart, setFetchStart] = useState('');
  const [fetchEnd, setFetchEnd] = useState('');
  const [loading, setLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [allClicks, setAllClicks] = useState([]);
  const [search, setSearch] = useState('');
  const [searchBy, setSearchBy] = useState('ip');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [gadsData, setGadsData] = useState(null);

  // Init date range
  useEffect(() => {
    const t = new Date(), fmt = d => d.toISOString().slice(0, 10);
    const s = new Date(t); s.setDate(s.getDate() - 30);
    setFetchStart(fmt(s)); setFetchEnd(fmt(t));
  }, []);

  const handleDateApply = (start, end, d) => {
    setDays(d); setFetchStart(start); setFetchEnd(end);
  };

  useEffect(() => {
    if (!campaignId || !fetchStart || !fetchEnd) { setData(null); setAllClicks([]); setGadsData(null); return; }
    let cancelled = false;
    const fetchData = async () => {
      if (!cancelled) { setLoading(true); setTabLoading(true); }
      try {
        const result = await analyticsAPI.getFraud(campaignId, days);
        if (!cancelled) { setData(deepNormalize(result)); setLastUpdated(new Date()); }
      } catch (e) { console.error('Analytics fetch error:', e); }
      try {
        const clicks = await clicksAPI.getRecent(campaignId, 500);
        if (!cancelled) setAllClicks(deepNormalize(clicks || []));
      } catch (e) { console.error('Clicks fetch error:', e); }
      try {
        const gads = await gadsScriptAPI.getDashboard(campaignId, fetchStart, fetchEnd);
        if (!cancelled) setGadsData(gads);
      } catch (e) {}
      if (!cancelled) { setLoading(false); setTabLoading(false); }
    };
    fetchData();
    const timer = setInterval(fetchData, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [campaignId, fetchStart, fetchEnd]);

  useEffect(() => { setPage(1); }, [activeTab, search, searchBy]);

  // Blocked IPs data
  const blockedClicks = useMemo(() =>
    allClicks.filter(c => c.is_fraud), [allClicks]);

  const filteredBlocked = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return blockedClicks;
    return blockedClicks.filter(c => {
      if (searchBy === 'ip') return (c.ip_address || '').toLowerCase().includes(s);
      if (searchBy === 'country') return (c.country || '').toLowerCase().includes(s);
      if (searchBy === 'reason') return getDetectionReason(c).toLowerCase().includes(s);
      return true;
    });
  }, [blockedClicks, search, searchBy]);

  // Clicks report data
  const filteredClicks = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return allClicks;
    return allClicks.filter(c => {
      if (searchBy === 'ip') return (c.ip_address || '').toLowerCase().includes(s);
      if (searchBy === 'country') return (c.country || '').toLowerCase().includes(s);
      return true;
    });
  }, [allClicks, search, searchBy]);

  // Multi-click distribution for donut
  const multiClickDist = useMemo(() => {
    if (!data?.multi_click_ips) return [];
    const dist = {};
    (data.multi_click_ips || []).forEach(ip => {
      const bucket = ip.total > 10 ? 'Over 10' : `${ip.total} Clicks`;
      dist[bucket] = (dist[bucket] || 0) + 1;
    });
    // Also count single-click IPs
    const totalIPs = new Set(allClicks.map(c => c.ip_address)).size;
    const multiIPs = (data.multi_click_ips || []).length;
    if (totalIPs > multiIPs) dist['1 Click'] = totalIPs - multiIPs;
    return Object.entries(dist).map(([name, value]) => ({ name, value }));
  }, [data, allClicks]);

  // Campaign breakdown from clicks — must be before conditional returns (React hooks rule)
  const campaignBreakdown = useMemo(() => {
    const map = {};
    allClicks.forEach(c => {
      const kw = String(c.campaign_name || c.utm_campaign || 'Unknown');
      if (!map[kw]) map[kw] = { name: kw, total: 0, fraud: 0 };
      map[kw].total++;
      if (c.is_fraud) map[kw].fraud++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 15);
  }, [allClicks]);

  // Keyword breakdown — must be before conditional returns (React hooks rule)
  const keywordBreakdown = useMemo(() => {
    const map = {};
    allClicks.forEach(c => {
      const kw = String(c.keyword || c.utm_term || c.search_query || 'Not provided');
      if (!map[kw]) map[kw] = { keyword: kw, total: 0, fraud: 0 };
      map[kw].total++;
      if (c.is_fraud) map[kw].fraud++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total).slice(0, 20);
  }, [allClicks]);

  const MULTI_COLORS = ['#7c3aed', '#06b6d4', '#ef4444', '#3b82f6', '#f97316', '#eab308', '#a855f7', '#ec4899', '#22c55e', '#6366f1', '#f472b6'];

  if (!campaignId) {
    return (
      <div>
        <div className="page-header"><h1>Google Ads Fraud Analytics</h1><p>Deep fraud analysis</p></div>
        <div className="empty">
          <h3>No campaign selected</h3>
          <p>Select a campaign from the sidebar to view fraud analytics.</p>
          <NavLink to="/campaigns" className="btn btn-primary" style={{ marginTop: 12, display: 'inline-block' }}>Go to Campaigns</NavLink>
        </div>
      </div>
    );
  }

  if (loading && !data) return (
    <div>
      <div className="page-header"><h1>Google Ads Fraud Analytics</h1></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="skeleton" style={{ height: 48, borderRadius: 10 }} />
        {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 56, borderRadius: 10, animationDelay: `${i*80}ms` }} />)}
      </div>
    </div>
  );
  if (!data) return <div className="empty"><h3>No data available</h3><p>Waiting for analytics data to load...</p></div>;

  const paginatedBlocked = filteredBlocked.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const paginatedClicks = filteredClicks.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const blockedPages = Math.ceil(filteredBlocked.length / rowsPerPage);
  const clickPages = Math.ceil(filteredClicks.length / rowsPerPage);

  // Fraud type donut data
  const fraudTypeData = Object.entries(data.blocked_reason_breakdown || {}).map(([name, value]) => ({ name: String(name).replace(/_/g, ' '), value: Number(value) || 0 }));

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Google Ads Fraud Analytics</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {fetchStart && fetchEnd
              ? `${new Date(fetchStart+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${new Date(fetchEnd+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`
              : `Last ${days} days`}
            {tabLoading && <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--accent-blue)' }}><div className="spinner" style={{ width: 11, height: 11, borderWidth: 2 }} /> Updating...</span>}
            {lastUpdated && !tabLoading && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>· updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <AnalyticsRangePicker onApply={handleDateApply} />
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); setSearch(''); setPage(1); }} style={{
            background: 'none', border: 'none', padding: '7px 14px', fontSize: 13, fontWeight: activeTab === tab ? 600 : 500,
            cursor: 'pointer', whiteSpace: 'nowrap',
            color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
            borderBottom: `2px solid ${activeTab === tab ? 'var(--accent-blue)' : 'transparent'}`,
            marginBottom: -1,
          }}>{tab}</button>
        ))}
      </div>

      {tabLoading && <TabLoader />}

      {/* ── Blocked IPs ── */}
      {!tabLoading && activeTab === 'Blocked IPs' && (
        <div>
          <div style={{ display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
            {/* Table */}
            <div style={{ flex: '1 1 500px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <SearchBar value={search} onChange={setSearch} placeholder="Search by IP, Last Click, or Block Reason" searchBy={searchBy} onSearchByChange={setSearchBy} />
                <DownloadButtons
                  onCSV={() => downloadCSV(blockedClicks.map(c => ({ ip: c.ip_address, last_click: c.created_at, reason: getDetectionReason(c) })), 'blocked_ips.csv')}
                />
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>IP Address</th><th>Last Click</th><th>Detection Reason</th><th></th></tr></thead>
                  <tbody>
                    {paginatedBlocked.length === 0 ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No blocked IPs found</td></tr>
                    ) : paginatedBlocked.map((c, i) => (
                      <tr key={i}>
                        <td><span className="ip-mono">{String(c.ip_address || '')}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{String(c.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{String(getDetectionReason(c))}</td>
                        <td><NavLink to="/clicks" style={{ fontSize: 11, color: 'var(--accent-orange)' }}>Show in Clicks Report</NavLink></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} totalPages={blockedPages} onChange={setPage} />
            </div>

            {/* Donut */}
            <div style={{ flex: '0 0 320px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, justifyContent: 'center' }}>
                {Object.entries(FRAUD_TYPE_COLORS).map(([name, color]) => (
                  <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                    <span style={{ color: 'var(--text-muted)' }}>{name}</span>
                  </div>
                ))}
              </div>
              {fraudTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={fraudTypeData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} dataKey="value" paddingAngle={2}>
                      {fraudTypeData.map((_, i) => <Cell key={i} fill={FRAUD_COLORS[i % FRAUD_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40, fontSize: 13 }}>No fraud detected yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Countries ── */}
      {!tabLoading && activeTab === 'Countries' && (
        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 500px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)', marginBottom: 8 }}>Last {data.total_clicks} clicks</div>
                <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, overflow: 'hidden' }}>
                  <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ scale: 110, center: [10, 20] }}
                    width={800} height={420}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  >
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) => geographies.map(geo => {
                        const countryName = geo.properties.name;
                        const countryData = (data.countries || []).find(c => c.country === countryName);
                        const fraud = countryData?.fraud || 0;
                        const maxFraud = Math.max(...(data.countries || []).map(c => c.fraud), 1);
                        const intensity = fraud > 0 ? 0.2 + (fraud / maxFraud) * 0.8 : 0;
                        return (
                          <Geography key={geo.rsmKey} geography={geo} fill={intensity > 0 ? `rgba(216,44,13,${intensity})` : '#e3e3e3'} stroke="#fff" strokeWidth={0.5}
                            style={{ default: { outline: 'none' }, hover: { fill: '#b02408', outline: 'none' }, pressed: { outline: 'none' } }} />
                        );
                      })}
                    </Geographies>
                  </ComposableMap>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                  <span>1</span>
                  <div style={{ flex: 1, height: 8, background: 'linear-gradient(to right, #fca5a5, #dc2626)', borderRadius: 4, maxWidth: 200 }} />
                  <span>{Math.max(...(data.countries || []).map(c => c.fraud), 0)}</span>
                </div>
              </div>
              <div style={{ flex: '0 0 320px' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-blue)', marginBottom: 8 }}>Top 5 Fraudulent Countries</div>
                {(data.countries || []).slice(0, 5).map((c, i) => {
                  const maxTotal = Math.max(...(data.countries || []).slice(0, 5).map(x => x.total), 1);
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', width: 80, textAlign: 'right', flexShrink: 0 }}>{c.country}</span>
                      <div style={{ flex: 1, display: 'flex', height: 16, borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${((c.total - c.fraud) / maxTotal) * 100}%`, background: '#22c55e' }} />
                        <div style={{ width: `${(c.fraud / maxTotal) * 100}%`, background: '#ef4444' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Keywords ── */}
      {!tabLoading && activeTab === 'Keywords' && (
        <div className="card">
          <div className="section-title">Keyword Performance</div>
          {keywordBreakdown.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No keyword data available. Keywords are captured from UTM parameters.</div>
          ) : (
            <>
              <DownloadButtons onCSV={() => downloadCSV(keywordBreakdown, 'keywords.csv')} />
              <div className="table-wrap" style={{ border: 'none', marginTop: 12 }}>
                <table>
                  <thead><tr><th>Keyword</th><th>Total Clicks</th><th>Fraud</th><th>Fraud Rate</th></tr></thead>
                  <tbody>
                    {keywordBreakdown.map((k, i) => {
                      const rate = k.total > 0 ? Math.round((k.fraud / k.total) * 100) : 0;
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight: 500, fontSize: 13 }}>{k.keyword}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{k.total}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--accent-red)' }}>{k.fraud}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{rate}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Campaigns ── */}
      {!tabLoading && activeTab === 'Campaigns' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* UTM campaign breakdown */}
          <div className="card">
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4, textTransform: 'none', letterSpacing: 0.5 }}>UTM Campaign Breakdown</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>Fraud vs genuine clicks by UTM campaign tag</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: '#ef4444' }} /><span>Fraud</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: '#22c55e' }} /><span>Genuine</span>
              </div>
            </div>
            {campaignBreakdown.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No campaign data available.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={Math.max(250, campaignBreakdown.length * 32)}>
                  <BarChart data={campaignBreakdown} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                    <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="fraud" name="Fraud" stackId="a" fill="#ef4444" />
                    <Bar dataKey="total" name="Genuine" stackId="a" fill="#22c55e" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 12 }}>
                  <DownloadButtons onCSV={() => downloadCSV(campaignBreakdown, 'campaigns_fraud.csv')} />
                </div>
              </>
            )}
          </div>

          {/* Google Ads per-campaign fraud estimates */}
          {gadsData && gadsData.has_data && gadsData.campaigns.length > 0 && (() => {
            const totalClicks = allClicks.length || 1;
            const fraudClicks = allClicks.filter(c => c.is_fraud).length;
            const fraudRate = fraudClicks / totalClicks;
            const maxClicks = Math.max(...gadsData.campaigns.map(c => c.clicks), 1);
            return (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <svg width="16" height="16" viewBox="0 0 48 48">
                    <path d="M33.6 43.2L43.2 26.4 33.6 9.6 24 26.4z" fill="#FBBC04"/>
                    <path d="M14.4 43.2L24 26.4 14.4 9.6 4.8 26.4z" fill="#4285F4"/>
                    <path d="M24 26.4L33.6 9.6H14.4L24 26.4z" fill="#34A853"/>
                  </svg>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'none', letterSpacing: 0.5 }}>Google Ads — Estimated Fraud Impact</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Based on {(fraudRate * 100).toFixed(1)}% overall fraud rate · {fetchStart} to {fetchEnd}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {gadsData.campaigns.map((c, i) => {
                    const estFraud = Math.round(c.clicks * fraudRate);
                    const estWasted = (estFraud * c.cpc).toFixed(2);
                    const fraudPct = c.clicks > 0 ? (estFraud / c.clicks) * 100 : 0;
                    const barW = (c.clicks / maxClicks) * 100;
                    return (
                      <div key={i} style={{ padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 120 }}>{c.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{c.clicks.toLocaleString()} clicks · ${c.cost.toLocaleString()} spend</span>
                          <span style={{ fontSize: 11, color: 'var(--accent-red)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>~{estFraud} fraud</span>
                          <span style={{ fontSize: 11, color: 'var(--accent-orange)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>~${estWasted} wasted</span>
                          <span style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: fraudPct > 5 ? 'var(--accent-red)' : 'var(--text-muted)', fontWeight: fraudPct > 5 ? 700 : 400 }}>{fraudPct.toFixed(1)}%</span>
                        </div>
                        <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--border)', position: 'relative' }}>
                          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barW}%`, background: 'var(--accent-green)', opacity: 0.25, borderRadius: 4 }} />
                          <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${barW * fraudPct / 100}%`, background: 'var(--accent-red)', borderRadius: 4 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 12 }}>
                  <DownloadButtons onCSV={() => downloadCSV(gadsData.campaigns.map(c => ({
                    campaign: c.name, clicks: c.clicks, spend: c.cost,
                    est_fraud_clicks: Math.round(c.clicks * fraudRate),
                    est_wasted: (Math.round(c.clicks * fraudRate) * c.cpc).toFixed(2),
                    fraud_pct: (c.clicks > 0 ? (Math.round(c.clicks * fraudRate) / c.clicks * 100) : 0).toFixed(1),
                  })), 'gads_campaign_fraud.csv')} />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Multi-Clicks ── */}
      {!tabLoading && activeTab === 'Multi-Clicks' && (
        <div>
          <div style={{ display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 320px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, justifyContent: 'center' }}>
                {multiClickDist.map((d, i) => (
                  <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: MULTI_COLORS[i % MULTI_COLORS.length] }} />
                    <span style={{ color: 'var(--text-muted)' }}>{d.name}</span>
                  </div>
                ))}
              </div>
              {multiClickDist.length > 0 && (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={multiClickDist} cx="50%" cy="50%" innerRadius={70} outerRadius={120} dataKey="value" paddingAngle={2}>
                      {multiClickDist.map((_, i) => <Cell key={i} fill={MULTI_COLORS[i % MULTI_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div style={{ flex: '1 1 400px' }}>
              <div className="table-wrap" style={{ border: 'none' }}>
                <table>
                  <thead><tr><th>Number of Clicks</th><th>Users</th><th>Percentage</th></tr></thead>
                  <tbody>
                    {multiClickDist.map((d, i) => {
                      const totalUsers = multiClickDist.reduce((s, x) => s + x.value, 0);
                      return (
                        <tr key={i}>
                          <td style={{ fontSize: 13 }}>{d.name}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{d.value}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{totalUsers > 0 ? (d.value / totalUsers * 100).toFixed(2) : 0}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── IP Ranges ── */}
      {!tabLoading && activeTab === 'IP Ranges' && (
        <div className="card">
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: 2, background: '#60a5fa' }} /><span>IP Range Clicks</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: 2, background: '#7c3aed' }} /><span>Number of IPs in Range</span>
            </div>
          </div>
          {(data.ip_ranges || []).length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 20, textAlign: 'center' }}>No IP range data available yet.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={Math.max(200, (data.ip_ranges || []).length * 45)}>
                <BarChart data={(data.ip_ranges || []).map(r => ({ ...r, display: r.range.replace('.0/24', '.*') }))} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <YAxis type="category" dataKey="display" width={100} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
                  <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="total" name="IP Range Clicks" fill="#60a5fa" />
                  <Bar dataKey="unique_ips" name="Number of IPs in Range" fill="#7c3aed" />
                </BarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: 12 }}>
                <DownloadButtons onCSV={() => downloadCSV((data.ip_ranges || []).map(r => ({ range: r.range, clicks: r.total, fraud: r.fraud, unique_ips: r.unique_ips })), 'ip_ranges.csv')} />
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Devices ── */}
      {!tabLoading && activeTab === 'Devices' && (
        <div>
          <div className="grid-2" style={{ marginBottom: 12 }}>
            <div className="card">
              <div className="section-title">By Device</div>
              <div className="table-wrap" style={{ border: 'none' }}>
                <table>
                  <thead><tr><th>Device</th><th>Total</th><th>Fraud</th><th>Fraud Rate</th></tr></thead>
                  <tbody>
                    {(data.devices || []).map((d, i) => {
                      const rate = d.total > 0 ? Math.round((d.fraud / d.total) * 100) : 0;
                      return (
                        <tr key={i}>
                          <td style={{ textTransform: 'capitalize', fontWeight: 500 }}>{d.device}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{d.total}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--accent-red)' }}>{d.fraud}</td>
                          <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{rate}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card">
              <div className="section-title">By Browser</div>
              <div className="table-wrap" style={{ border: 'none' }}>
                <table>
                  <thead><tr><th>Browser</th><th>Total</th><th>Fraud</th></tr></thead>
                  <tbody>
                    {(data.browsers || []).map((b, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{b.browser}</td>
                        <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>{b.total}</td>
                        <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, color: 'var(--accent-red)' }}>{b.fraud}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {(data.os_breakdown || []).length > 0 && (
            <div className="card">
              <div className="section-title">By OS</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.os_breakdown} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="os" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="total" name="Total" fill="#58a6ff" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="fraud" name="Fraud" fill="#f85149" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <DownloadButtons onCSV={() => downloadCSV((data.devices || []).map(d => ({ device: d.device, total: d.total, fraud: d.fraud })), 'devices.csv')} />
          </div>
        </div>
      )}

      {/* ── Clicks Report ── */}
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="section-title" style={{ marginBottom: 0 }}>Clicks Report</div>
          <DownloadButtons
            onCSV={() => downloadCSV(allClicks.map(c => ({
              ip: c.ip_address, country: c.country, device: c.device_type,
              is_fraud: c.is_fraud ? 'Yes' : 'No', fraud_score: c.fraud_score,
              created_at: c.created_at, reason: getDetectionReason(c),
            })), 'clicks_report.csv')}
            onBlock={() => downloadCSV(blockedClicks.map(c => ({
              ip: c.ip_address, country: c.country, reason: getDetectionReason(c),
              blocked_at: c.created_at,
            })), 'block_report.csv')}
          />
        </div>

        <SearchBar value={search} onChange={setSearch} placeholder="Search by IP or Country" searchBy={searchBy} onSearchByChange={setSearchBy} />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }} style={{ width: 'auto', fontSize: 12 }}>
            <option value={10}>10 rows</option>
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Page {page} of {clickPages || 1}</span>
        </div>

        <div className="table-wrap" style={{ border: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>IP Address</th>
                <th>Blocked</th>
                <th>Clicks</th>
                <th>Country</th>
                <th>Last Click</th>
              </tr>
            </thead>
            <tbody>
              {paginatedClicks.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>No clicks found</td></tr>
              ) : paginatedClicks.map((c, i) => (
                <tr key={i}>
                  <td><span className="ip-mono">{String(c.ip_address || '')}</span></td>
                  <td>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
                      borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: c.is_fraud ? 'rgba(248,81,73,0.1)' : 'rgba(63,185,80,0.1)',
                      color: c.is_fraud ? 'var(--accent-red)' : 'var(--accent-green)',
                    }}>
                      {c.is_fraud ? 'Blocked' : 'Clean'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>1</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {String(c.country || 'Unknown')}
                  </td>
                  <td style={{ fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {String(c.created_at || '').slice(0, 19).replace('T', ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={clickPages} onChange={setPage} />
      </div>
    </div>
  );
}
